import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconEye as Eye, IconEyeOff as EyeClosed } from '@tabler/icons-react';
import {
  Button,
  Description,
  FieldError,
  Input as HeroUiInput,
  InputGroup,
  Label,
  TextField,
  toast,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { useAppDispatch } from '@/stores/hooks';
import { setCredentials } from '@hs/shared';
import { handleTRPCError } from '@/utils/error-handler';
import { handleError } from '@hs/shared';

type Step = 1 | 2;

const SMS_COOLDOWN_SECONDS = 60;

function Input(props: Record<string, unknown>) {
  return <HeroUiInput {...(props as object)} />;
}
const SMS_SCENE = 'REGISTER' as const;
const MOBILE_RE = /^1[3-9]\d{9}$/;

const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 6, msg: '不少于6位' },
  { test: (v: string) => /[a-z]/.test(v), msg: '须包含小写字母' },
  { test: (v: string) => /[A-Z]/.test(v), msg: '须包含大写字母' },
  { test: (v: string) => /\d/.test(v), msg: '须包含数字' },
  { test: (v: string) => /[^a-zA-Z0-9]/.test(v), msg: '须包含特殊符号' },
];

function passwordError(password: string): string | null {
  for (const rule of PASSWORD_RULES) {
    if (!rule.test(password)) return rule.msg;
  }
  return null;
}

function nicknameError(nickname: string): string | null {
  const trimmed = nickname.trim();
  if (!trimmed) return '请填写昵称';
  const hanCount = trimmed.match(/[\u4e00-\u9fa5]/g)?.length ?? 0;
  const latinCount = trimmed.match(/[a-zA-Z]/g)?.length ?? 0;
  if (hanCount < 2 && latinCount < 2) {
    return '不少于两个汉字或两个英文字母';
  }
  return null;
}

function saveAuthAndRedirect(
  dispatch: ReturnType<typeof useAppDispatch>,
  navigate: ReturnType<typeof useNavigate>,
  data: {
    accessToken: string;
    refreshToken?: string;
    user: {
      id: string;
      username?: string | null;
      nickname?: string | null;
      email?: string | null;
      mobile?: string | null;
      avatarUrl?: string | null;
      status?: string | null;
      roles?: unknown;
      apps?: unknown;
      spaces?: unknown;
    };
  },
  spaceName: string
) {
  dispatch(
    setCredentials({
      user: {
        id: data.user.id,
        username: data.user.username ?? null,
        nickname: data.user.nickname ?? undefined,
        email: data.user.email ?? undefined,
        mobile: data.user.mobile ?? undefined,
        avatarUrl: data.user.avatarUrl ?? undefined,
        status: data.user.status ?? undefined,
        roles: data.user.roles as never,
        apps: data.user.apps as never,
        spaces: data.user.spaces as never,
      },
      token: data.accessToken,
      refreshToken: data.refreshToken,
    })
  );
  toast.success('欢迎加入', {
    description: `您已成功进入${spaceName}`,
  });
  navigate('/');
}

function FullScreenMessage({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-default-50 to-default-100 p-4">
      <p className="text-center text-lg text-foreground">{message}</p>
    </div>
  );
}

export default function InviterRegisterPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code')?.trim() ?? '';

  const [step, setStep] = useState<Step>(1);
  const [mobile, setMobile] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const [mobileError, setMobileError] = useState<string | null>(null);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [passwordErrorText, setPasswordErrorText] = useState<string | null>(null);
  const [nicknameErrorText, setNicknameErrorText] = useState<string | null>(null);

  const [smsCooldown, setSmsCooldown] = useState(0);
  const [canSendSms, setCanSendSms] = useState(false);
  const [smsVerifying, setSmsVerifying] = useState(false);

  const spaceQuery = trpc.invitationCodes.getSpaceByCode.useQuery(
    { code },
    {
      enabled: !!code,
      retry: false,
      // 页面已全屏展示「邀请码错误」；跳过全局 toast，避免 StrictMode 双次请求重复弹窗
      meta: { suppressGlobalError: true },
    }
  );

  useEffect(() => {
    if (smsCooldown <= 0) return;
    const t = window.setTimeout(() => setSmsCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [smsCooldown]);

  const sendSmsMutation = trpc.auth.sendSmsCode.useMutation({
    onSuccess: () => {
      setSmsCooldown(SMS_COOLDOWN_SECONDS);
      toast.success('验证码已发送');
    },
    onError: (error) => handleTRPCError(error),
  });

  const verifyMutation = trpc.auth.invitationRegisterVerify.useMutation({
    onError: (error) => handleTRPCError(error),
  });

  const completeMutation = trpc.auth.invitationRegisterComplete.useMutation({
    onError: (error) => handleTRPCError(error),
  });

  const spaceName = spaceQuery.data?.spaceName ?? '';

  const handleMobileChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    setMobile(digits);
    setMobileError(null);
    if (digits.length === 11 && MOBILE_RE.test(digits)) {
      setCanSendSms(true);
    } else {
      setCanSendSms(false);
      if (digits.length === 11) {
        setMobileError('手机号格式有误，请修正');
      }
    }
  }, []);

  const handleSendSms = async () => {
    if (!canSendSms || smsCooldown > 0 || !code) return;
    await sendSmsMutation.mutateAsync({ mobile, scene: SMS_SCENE, forInvitationRegister: true });
  };

  const finishWithAuth = useCallback(
    (data: Parameters<typeof saveAuthAndRedirect>[2]) => {
      saveAuthAndRedirect(dispatch, navigate, data, spaceName);
    },
    [dispatch, navigate, spaceName]
  );

  const goStep2 = async () => {
    setSmsError(null);
    if (!MOBILE_RE.test(mobile)) {
      setMobileError('手机号格式有误，请修正');
      return;
    }
    if (!smsCode.trim()) {
      setSmsError('请输入验证码');
      return;
    }

    setSmsVerifying(true);
    try {
      const result = await verifyMutation.mutateAsync({
        code,
        mobile,
        smsCode: smsCode.trim(),
      });

      if ('needsProfile' in result && result.needsProfile) {
        setStep(2);
        return;
      }

      if ('accessToken' in result) {
        finishWithAuth(result);
      }
    } catch (error) {
      const { message } = handleError(error);
      setSmsError(message || '验证码有误，请检查');
    } finally {
      setSmsVerifying(false);
    }
  };

  const handlePasswordBlur = () => {
    setPasswordErrorText(passwordError(password));
  };

  const handleNicknameBlur = () => {
    setNicknameErrorText(nicknameError(nickname));
  };

  const handleSubmit = async () => {
    const nickErr = nicknameError(nickname);
    const pwdErr = passwordError(password);
    setNicknameErrorText(nickErr);
    setPasswordErrorText(pwdErr);
    if (nickErr || pwdErr) return;

    try {
      const result = await completeMutation.mutateAsync({
        code,
        mobile,
        smsCode: smsCode.trim(),
        password,
        nickname: nickname.trim(),
      });
      finishWithAuth(result);
    } catch {
      // handled by mutation onError
    }
  };

  if (!code) {
    return <FullScreenMessage message="无邀请码，不支持访问本页面" />;
  }

  if (spaceQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-default-50 to-default-100">
        <p className="text-muted-foreground text-sm">加载中…</p>
      </div>
    );
  }

  if (spaceQuery.isError || !spaceQuery.data) {
    return <FullScreenMessage message="邀请码错误，请检查链接" />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-default-50 to-default-100 p-4">
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-default-200 bg-content1 p-8 shadow-sm">
          <div className="mb-6 text-center">
            <p className="text-base text-foreground">
              <span className="font-bold text-accent">{spaceName}</span>
              邀请您
            </p>
            <h1 className="mt-3 text-2xl font-bold text-foreground">注册人文派账号</h1>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <TextField className="w-full flex flex-col gap-1" isInvalid={!!mobileError}>
                <Label className="text-sm font-medium">手机号</Label>
                <Input
                  placeholder="输入手机号"
                  inputMode="numeric"
                  value={mobile}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleMobileChange(e.target.value)}
                />
                {mobileError ? <FieldError>{mobileError}</FieldError> : null}
              </TextField>

              <TextField className="w-full flex flex-col gap-1" isInvalid={!!smsError}>
                <Label className="text-sm font-medium">验证码</Label>
                <InputGroup aria-label="短信验证码" className="w-full">
                  <InputGroup.Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="输入验证码"
                    value={smsCode}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setSmsError(null);
                    }}
                    autoComplete="one-time-code"
                  />
                  <InputGroup.Suffix className="px-1">
                    <button
                      type="button"
                      className="shrink-0 whitespace-nowrap px-2 text-sm font-medium text-primary disabled:cursor-not-allowed disabled:text-default-400"
                      disabled={
                        !canSendSms || smsCooldown > 0 || sendSmsMutation.isPending || smsVerifying
                      }
                      onClick={() => void handleSendSms()}
                    >
                      {sendSmsMutation.isPending
                        ? '发送中…'
                        : smsCooldown > 0
                          ? `${smsCooldown}s`
                          : '获取验证码'}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
                {smsError ? <FieldError>{smsError}</FieldError> : null}
              </TextField>

              <Button
                variant="primary"
                className="w-full"
                size="lg"
                isDisabled={smsVerifying}
                onPress={() => void goStep2()}
              >
                {smsVerifying ? '验证中…' : '下一步'}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <TextField className="w-full flex flex-col gap-1" isInvalid={!!passwordErrorText}>
                <Label className="text-sm font-medium">密码</Label>
                <InputGroup aria-label="密码" className="w-full">
                  <InputGroup.Input
                    type={isPasswordVisible ? 'text' : 'password'}
                    placeholder="输入密码"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    onBlur={handlePasswordBlur}
                  />
                  <InputGroup.Suffix className="px-1">
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      className="text-default-500"
                      aria-label="切换密码可见性"
                    >
                      {isPasswordVisible ? (
                        <EyeClosed className="h-4 w-4" stroke={1.5} />
                      ) : (
                        <Eye className="h-4 w-4" stroke={1.5} />
                      )}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
                <Description className="text-xs text-default-500">
                  不少于6位，须包含大小写字母、数字及特殊符号
                </Description>
                {passwordErrorText ? <FieldError>{passwordErrorText}</FieldError> : null}
              </TextField>

              <TextField className="w-full flex flex-col gap-1" isInvalid={!!nicknameErrorText}>
                <Label className="text-sm font-medium">昵称</Label>
                <Input
                  placeholder="为自己设置一个昵称"
                  value={nickname}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setNickname(e.target.value)}
                  onBlur={handleNicknameBlur}
                />
                <Description className="text-xs text-default-500">
                  不少于两个汉字或两个英文字母
                </Description>
                {nicknameErrorText ? <FieldError>{nicknameErrorText}</FieldError> : null}
              </TextField>

              <div className="flex gap-3">
                <Button variant="tertiary" className="flex-1" onPress={() => setStep(1)}>
                  上一步
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  isDisabled={completeMutation.isPending}
                  onPress={() => void handleSubmit()}
                >
                  {completeMutation.isPending ? '注册中…' : '完成注册'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
